import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';
import { logAPIError, formatAPIError } from '../utils/apiErrorLogger';

// Helper function to convert date to US Eastern timezone
const convertToEasternTime = (date) => {
  if (!date) return null;
  const inputDate = new Date(date);
  // Ensure we're working with a valid date
  if (isNaN(inputDate.getTime())) return null;
  
  // Create date string in Eastern timezone
  const easternDate = new Date(inputDate.toLocaleString('en-US', {
    timeZone: 'America/New_York'
  }));
  
  // Add timezone offset to compensate for local conversion
  const offset = inputDate.getTimezoneOffset();
  easternDate.setMinutes(easternDate.getMinutes() + offset);
  
  // Format as YYYY-MM-DD
  const year = easternDate.getFullYear();
  const month = String(easternDate.getMonth() + 1).padStart(2, '0');
  const day = String(easternDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date in YYYY-MM-DD in Eastern timezone
const formatDateToEasternYYYYMMDD = (date) => {
  if (!date) return null;
  return convertToEasternTime(date);
};

// Time Entry JSON Schema
const createEmptyTimeEntry = () => ({
  timeEntryId: '',
  contractId: '',
  date: formatDateToEasternYYYYMMDD(new Date()),
  hours: 0,
  description: '',
  createdBy: '',
  createdDate: '',
  modifiedBy: '',
  modifiedDate: ''
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

export const timeEntryService = {
  // Create empty time entry
  createEmptyTimeEntry,

  // Get all time entries
  async getTimeEntries(pageSize = 10000) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/timeentries?pageSize=${pageSize}&pageNumber=1`);
      const responseData = await handleResponse(response);
      
      // Convert dates to Eastern timezone and normalize field names
      const transformedData = responseData.data.map(entry => ({
        id: entry.Id || entry.id,
        date: entry.Date ? convertToEasternTime(entry.Date) : null,
        description: entry.Description || entry.description || '',
        hours: parseFloat(entry.Hours || entry.hours || 0),
        userEmail: entry.UserEmail || entry.userEmail
      }));

      // Return paginated response structure
      return {
        data: transformedData,
        totalCount: responseData.totalCount,
        pageNumber: responseData.pageNumber,
        pageSize: responseData.pageSize
      };
    } catch (error) {
      logAPIError('timeEntryService', 'getTimeEntries', 'GET', error);
      throw error;
    }
  },

  // Get time entry by ID
  async getTimeEntry(timeEntryId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/timeentries/${timeEntryId}`);
      const data = await handleResponse(response);
      
      // Convert date to Eastern timezone and normalize field names
      return {
        id: data.Id || data.id,
        date: data.Date ? convertToEasternTime(data.Date) : null,
        description: data.Description || data.description || '',
        hours: parseFloat(data.Hours || data.hours || 0),
        userEmail: data.UserEmail || data.userEmail
      };
    } catch (error) {
      logAPIError('timeEntryService', 'getTimeEntry', 'GET', error, { timeEntryId });
      throw error;
    }
  },

  // Save time entry (create or update)
  async saveTimeEntry(timeEntry, userEmail, timeEntryId = null) {
    try {
      const { apiBaseUrl } = getApiConfig();
      
      const apiTimeEntry = {
        timeEntryId: timeEntryId || timeEntry.timeEntryId || uuidv4(),
        date: timeEntry.date,
        hours: timeEntry.hours,
        description: timeEntry.description,
        userEmail: userEmail,
        talentId: timeEntry.talentId,
        supportContractId: timeEntry.supportContractId,
        createdBy: timeEntry.createdBy,
        modifiedBy: timeEntry.modifiedBy
      };

      const response = await fetch(`${apiBaseUrl}/timeEntries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiTimeEntry)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logAPIError('timeEntryService', 'saveTimeEntry', 'POST', error, {
        timeEntry,
        userEmail,
        timeEntryId
      });
      throw error;
    }
  },

  // Delete time entry
  async deleteTimeEntry(timeEntryId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/timeentries/${timeEntryId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      logAPIError('timeEntryService', 'deleteTimeEntry', 'DELETE', error, { timeEntryId });
      throw error;
    }
  },

  // Get time entries by user and month (optimized single call)
  async getTimeEntriesByUserAndMonth(userEmail, year, month) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(
        `${apiBaseUrl}/timeentries/user/${encodeURIComponent(userEmail)}/month/${year}/${month + 1}`
      );
      const data = await handleResponse(response);
      
      // Convert dates to Eastern timezone and normalize field names
      return data.map(entry => ({
        id: entry.Id || entry.id,
        date: entry.Date ? convertToEasternTime(entry.Date) : null,
        description: entry.Description || entry.description || '',
        hours: parseFloat(entry.Hours || entry.hours || 0),
        userEmail: entry.UserEmail || entry.userEmail
      }));
    } catch (error) {
      logAPIError('timeEntryService', 'getTimeEntriesByUserAndMonth', 'GET', error, {
        userEmail,
        year,
        month
      });
      throw error;
    }
  }
}; 